import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Section } from '@/api/courses';
import { BlockBoundary } from '@/components/lesson/BlockBoundary';
import { BlockView } from '@/components/lesson/BlockView';
import { computeNumbers } from '@/components/lesson/blockNumbering';
import { PreviewSandboxContext } from '@/components/lesson/previewSandbox';
import { Button } from '@/components/ui/Button';
import { titleHtml, titleText } from '@/lib/blockTitle';
import { cn } from '@/lib/utils';

/**
 * Предпросмотр ученика прямо в конструкторе — как в edvibe (D39, 13.09.2026).
 *
 * Урок рисуется ТЕМИ ЖЕ блоками, что у ученика (`BlockView mode="student"`), в той
 * же обёртке задания (`ex-section` / `ex-blockrow` / `ex-head`), из ЧЕРНОВИКА — видны
 * несохранённые правки. У раздела и у каждого задания — «Редактировать»: возвращает в
 * редактор ровно к этому месту.
 *
 * ⚠️ ОТВЕЧАТЬ МОЖНО, СОХРАНЯТЬ НЕЛЬЗЯ (решение владельца 13.09). `onAnswer` здесь
 * ничего не шлёт: ни прогресса, ни очереди отправки, ни пульса урока. Поэтому из
 * плеера ученика взяты только блоки, а не страница: страница стартует урок, шлёт
 * пульс, держит очередь ответов и тянет чаты в боковую панель — всё это от учётки
 * сотрудника.
 *
 * ⚠️ ЗАПИСЬ ГОЛОСА НЕ РИСУЕТСЯ — вместо неё заглушка. Рекордер просит микрофон, заводит
 * черновик в хранилище браузера и держит экран; в предпросмотре это не нужно, а трогать
 * сам рекордер ради песочницы нельзя — у него своя полоса (F-ученик).
 *
 * ⚠️ Блоки, которые пишут на сервер мимо `onAnswer` (набор слов — «в словарь»), видят
 * `PreviewSandboxContext` и в песочнице эти кнопки прячут.
 */
export function StudentPreview({
  sections,
  activeSectionId,
  onSelectSection,
  onEdit,
}: {
  sections: Section[];
  activeSectionId: string | null;
  onSelectSection: (id: string) => void;
  /** Вернуться в редактор: к разделу (`blockId` null) или к заданию. */
  onEdit: (sectionId: string, blockId: string | null) => void;
}) {
  const { t } = useTranslation();
  // «Сбросить ответы» — новый ключ раздела: блоки монтируются заново и забывают ответы.
  const [attempt, setAttempt] = useState(0);
  const numbers = useMemo(() => computeNumbers(sections), [sections]);

  const active = sections.find((s) => s.id === activeSectionId) ?? sections[0];
  if (!active) return null;
  const idx = sections.indexOf(active);
  const blocks = active.blocks ?? [];

  return (
    <PreviewSandboxContext.Provider value>
      <div className="mx-auto max-w-3xl">
        {/* min-w: на 390 пояснение сжималось в столбик по слову рядом с «Сбросить ответы» —
            теперь кнопка уходит на следующую строку. */}
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card bg-surface-container px-4 py-3 text-sm text-on-surface-variant">
          <span className="material-symbols-outlined text-base">visibility</span>
          <span className="min-w-[12rem] flex-1">{t('lessonBuilder.previewNote')}</span>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="inline-flex shrink-0 items-center gap-1 font-medium text-primary hover:underline"
          >
            <span className="material-symbols-outlined text-base">refresh</span>
            {t('lessonBuilder.previewReset')}
          </button>
        </div>

        <section key={`${active.id}:${attempt}`}>
          {/* flex-wrap: на 390 «Редактировать раздел» наезжала на название раздела. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {active.is_homework && <span className="material-symbols-outlined text-tertiary">home</span>}
            {/* min-w, а не min-w-0: иначе название сжимается под кнопку, а не переносит её вниз. */}
            <h2 className="min-w-[10rem] flex-1 font-display text-2xl font-bold tracking-display text-on-surface">
              {active.is_homework ? t('lessonPlayer.homeworkTitle') : active.title}
            </h2>
            <EditButton label={t('lessonBuilder.previewEditSection')} onClick={() => onEdit(active.id, null)} />
          </div>

          {blocks.length === 0 ? (
            <p className="py-8 text-center text-sm text-on-surface-variant">{t('lessonBuilder.previewNoBlocks')}</p>
          ) : (
            <div className="ex-section">
              {blocks.map((b) => {
                const num = numbers.get(b.id);
                const hasTitle = titleText(b.content.title) !== '';
                return (
                  <div key={b.id} className="ex-blockrow relative" data-preview-block-id={b.id}>
                    {/* На 390 кнопка — обычной строкой над заданием: в углу, с отступом под неё,
                        заголовок шёл по одному слову в строку. С ширины sm — в правом верхнем углу. */}
                    <div className="mb-2 flex justify-end sm:absolute sm:right-3 sm:top-3 sm:z-10 sm:mb-0">
                      <EditButton label={t('lessonBuilder.previewEdit')} onClick={() => onEdit(active.id, b.id)} />
                    </div>
                    {(num || hasTitle) && (
                      // sm:pr — место под «Редактировать», чтобы длинный заголовок под неё не заезжал.
                      <div className="ex-head sm:pr-32">
                        {num && <span className="ex-num">{num}</span>}
                        {hasTitle && (
                          <h3 className="ex-title" dangerouslySetInnerHTML={{ __html: titleHtml(b.content.title) }} />
                        )}
                      </div>
                    )}
                    <div className={cn(!num && !hasTitle && 'sm:pr-32')}>
                      <BlockBoundary
                        resetKey={b.content}
                        fallback={<p className="py-3 text-sm text-on-surface-variant">{t('lessonBuilder.previewBlockError')}</p>}
                      >
                        {b.type === 'audio_record' ? (
                          <p className="flex items-center gap-2 rounded-card bg-surface-container px-4 py-3 text-sm text-on-surface-variant">
                            <span className="material-symbols-outlined">mic_off</span>
                            {t('lessonBuilder.previewRecordOff')}
                          </p>
                        ) : (
                          <BlockView block={b} mode="student" onAnswer={() => undefined} />
                        )}
                      </BlockBoundary>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div className="mt-6 flex flex-wrap justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={idx <= 0}
            onClick={() => idx > 0 && onSelectSection(sections[idx - 1].id)}
          >
            <span className="material-symbols-outlined">chevron_left</span>
            {t('lessonBuilder.previewPrev')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={idx >= sections.length - 1}
            onClick={() => idx < sections.length - 1 && onSelectSection(sections[idx + 1].id)}
          >
            {t('lessonBuilder.previewNext')}
            <span className="material-symbols-outlined">chevron_right</span>
          </Button>
        </div>
      </div>
    </PreviewSandboxContext.Provider>
  );
}

function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-surface-container-lowest px-3 py-1 text-xs font-semibold text-primary shadow-soft transition hover:bg-primary-container hover:text-on-primary-container"
    >
      <span className="material-symbols-outlined text-sm">edit</span>
      {label}
    </button>
  );
}
